import { DataTable, type DataTableColumn } from "@design";
import { useTranslation } from "react-i18next";
import type { TopAgentEntry, TopToolEntry, UsageByModel, UsageByTeam } from "@/api";
import { BarRow, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatCompact, formatNumber, formatUsd } from "@/i18n/format";

function Empty({ text }: Readonly<{ text: string }>) {
  return (
    <Text variant="body-sm" tone="tertiary">
      {text}
    </Text>
  );
}

function Bars({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="flex flex-col gap-4">{children}</div>;
}

export function UsageByTeamSection({ teams }: Readonly<{ teams: UsageByTeam[] }>) {
  const { t } = useTranslation("adminUsage");
  const max = Math.max(0, ...teams.map((team) => team.estimatedCostUsd));
  return (
    <PageSection title={t("overview.byTeam")}>
      {teams.length === 0 ? (
        <Empty text={t("overview.byTeamEmpty")} />
      ) : (
        <Bars>
          {teams.map((team) => (
            <BarRow
              key={team.teamUuid}
              label={team.teamName}
              value={t("overview.teamValue", {
                cost: formatUsd(team.estimatedCostUsd),
                requests: formatNumber(team.requests),
              })}
              fraction={max > 0 ? team.estimatedCostUsd / max : 0}
            />
          ))}
        </Bars>
      )}
    </PageSection>
  );
}

export function UsageByModelSection({ models }: Readonly<{ models: UsageByModel[] }>) {
  const { t } = useTranslation("adminUsage");
  const numeric = (value: string) => (
    <Text as="span" variant="body-sm" tone="secondary" className="tabular-nums">
      {value}
    </Text>
  );
  const columns: DataTableColumn<UsageByModel>[] = [
    {
      key: "model",
      header: t("overview.modelColumns.model"),
      mobile: "primary",
      render: (row) => (
        <Text as="span" variant="body-sm">
          {row.modelName || row.modelId}
        </Text>
      ),
    },
    {
      key: "requests",
      header: t("overview.modelColumns.requests"),
      align: "right",
      render: (row) => numeric(formatNumber(row.requests)),
    },
    {
      key: "tokens",
      header: t("overview.modelColumns.tokens"),
      align: "right",
      render: (row) => numeric(formatCompact(row.tokensInput + row.tokensOutput)),
    },
    {
      key: "cost",
      header: t("overview.modelColumns.cost"),
      align: "right",
      render: (row) => numeric(formatUsd(row.estimatedCostUsd)),
    },
  ];
  return (
    <PageSection title={t("overview.byModel")}>
      <DataTable
        aria-label={t("overview.byModel")}
        columns={columns}
        rows={models}
        rowKey={(row) => row.modelId}
        empty={<Empty text={t("overview.byModelEmpty")} />}
      />
    </PageSection>
  );
}

export function TopToolsSection({ tools }: Readonly<{ tools: TopToolEntry[] }>) {
  const { t } = useTranslation("adminUsage");
  const max = Math.max(0, ...tools.map((tool) => tool.executions));
  return (
    <PageSection title={t("overview.topTools")}>
      {tools.length === 0 ? (
        <Empty text={t("overview.topToolsEmpty")} />
      ) : (
        <Bars>
          {tools.map((tool) => (
            <BarRow
              key={tool.toolName}
              label={tool.toolName}
              value={t("overview.toolValue", {
                count: tool.executions,
                rate: formatNumber(tool.successRate, { style: "percent", maximumFractionDigits: 0 }),
              })}
              fraction={max > 0 ? tool.executions / max : 0}
              tone="secondary"
            />
          ))}
        </Bars>
      )}
    </PageSection>
  );
}

export function TopAgentsSection({ agents }: Readonly<{ agents: TopAgentEntry[] }>) {
  const { t } = useTranslation("adminUsage");
  const max = Math.max(0, ...agents.map((agent) => agent.uses));
  return (
    <PageSection title={t("overview.topAgents")}>
      {agents.length === 0 ? (
        <Empty text={t("overview.topAgentsEmpty")} />
      ) : (
        <Bars>
          {agents.map((agent) => (
            <BarRow
              key={agent.agentId ?? agent.agentName}
              label={agent.agentName}
              value={t("overview.agentValue", { count: agent.uses })}
              fraction={max > 0 ? agent.uses / max : 0}
              tone="primary"
            />
          ))}
        </Bars>
      )}
    </PageSection>
  );
}
